!(function () {
  'use strict';

  if (customElements.get('social-videos')) return;

  const SOUND_LABEL_ON = 'Slå lyd fra';
  const SOUND_LABEL_OFF = 'Slå lyd til';
  const PREVIEW_LOOP_SECONDS = 2;

  const STATE_IDLE = 'idle';
  const STATE_PREVIEW = 'preview';
  const STATE_PLAYING = 'playing';
  const STATE_PAUSED = 'paused';

  customElements.define(
    'social-videos',
    class SocialVideos extends HTMLElement {
      static allSections = [];

      connectedCallback() {
        SocialVideos.allSections.push(this);
        this.videoItems = this.querySelectorAll('[data-social-video-item]');
        this.videoItems?.forEach((item, index) => this.initVideoItem(item, index));
      }

      disconnectedCallback() {
        const i = SocialVideos.allSections.indexOf(this);
        if (i > -1) SocialVideos.allSections.splice(i, 1);
      }

      initVideoItem(item, index) {
        const video = item.querySelector('video');
        const playButton = item.querySelector('[data-video-play]');
        if (!video || !playButton) return;

        item._video = video;
        item._playButton = playButton;
        item._soundButton = item.querySelector('[data-video-sound]');
        item._index = index;
        item._state = STATE_IDLE;
        // User's persistent sound preference for this item. Defaults to on —
        // first click on the center play button plays with sound (the click is a
        // user gesture, which the browser accepts as permission to unmute).
        // Only the sound button can flip it back to muted.
        item._userWantsSound = true;

        // Initial state: muted (matches video_tag muted: true)
        video.muted = true;
        this.syncMutedClass(item);

        item.addEventListener('mouseenter', () => {
          if (item._state === STATE_IDLE) this.startPreview(item);
        });

        item.addEventListener('mouseleave', () => {
          if (item._state === STATE_PREVIEW) this.stopPreview(item);
        });

        // Loop the first PREVIEW_LOOP_SECONDS while previewing
        video.addEventListener('timeupdate', () => {
          if (item._state === STATE_PREVIEW && video.currentTime >= PREVIEW_LOOP_SECONDS) {
            try {
              video.currentTime = 0;
            } catch (e) {}
          }
        });

        const togglePlay = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (item._state === STATE_PLAYING) {
            this.pauseVideo(item);
          } else {
            this.startPlayback(item);
          }
        };
        playButton.addEventListener('click', togglePlay);
        playButton.addEventListener('touchstart', togglePlay);

        if (item._soundButton) {
          const soundToggle = (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleSound(item);
          };
          item._soundButton.addEventListener('click', soundToggle);
          item._soundButton.addEventListener('touchstart', soundToggle);
        }

        video.addEventListener('volumechange', () => this.syncMutedClass(item));
      }

      startPreview(item) {
        item._state = STATE_PREVIEW;
        item._video.muted = true;
        try {
          item._video.currentTime = 0;
        } catch (e) {}
        item.classList.add('is-previewing');
        item.classList.remove('is-playing');
        item.classList.remove('is-paused');
        item._video.play().catch(() => {});
        this.syncMutedClass(item);
      }

      stopPreview(item) {
        item._state = STATE_IDLE;
        item._video.pause();
        try {
          item._video.currentTime = 0;
        } catch (e) {}
        item.classList.remove('is-previewing');
      }

      startPlayback(item) {
        this.pauseAllOthers(item);
        const previousState = item._state;
        item._state = STATE_PLAYING;

        // Honor the persistent user sound preference.
        item._video.muted = !item._userWantsSound;
        if (item._userWantsSound) item._video.volume = 1;

        const onSuccess = () => {
          item.classList.add('is-playing');
          item.classList.remove('is-previewing');
          item.classList.remove('is-paused');
          item._playButton.setAttribute('aria-label', theme.strings.pauseVideo);
          this.syncMutedClass(item);
        };

        const playPromise = item._video.play();
        if (playPromise !== undefined) {
          playPromise.then(onSuccess).catch((err) => {
            // If play fails (e.g. unmuted blocked by browser), force mute and retry once.
            if (!item._video.muted) {
              item._video.muted = true;
              item._video.play().then(onSuccess).catch(() => {
                item._state = previousState;
                console.log('Video playback prevented:', err);
              });
            } else {
              item._state = previousState;
              console.log('Video playback prevented:', err);
            }
          });
        } else {
          onSuccess();
        }
      }

      // Soft pause — preserves currentTime and sound preference so play resumes from where the user stopped.
      pauseVideo(item) {
        if (item._state !== STATE_PLAYING) return;
        item._state = STATE_PAUSED;
        item._video.pause();
        item.classList.remove('is-playing');
        item.classList.remove('is-previewing');
        item.classList.add('is-paused');
        item._playButton.setAttribute('aria-label', theme.strings.playVideo);
      }

      toggleSound(item) {
        if (!item._video) return;
        const wantSound = !item._userWantsSound;
        item._userWantsSound = wantSound;
        item._video.muted = !wantSound;
        if (wantSound) item._video.volume = 1;

        if (wantSound) {
          // If not in full playback yet, kick off full playback so the user actually hears something.
          if (item._state !== STATE_PLAYING) {
            this.startPlayback(item);
          } else {
            this.pauseAllOthers(item);
          }
        }
        this.syncMutedClass(item);
      }

      pauseAllOthers(currentItem) {
        SocialVideos.allSections.forEach((section) => {
          section.videoItems?.forEach((item) => {
            if (item === currentItem || !item._video) return;
            if (item._state === STATE_PLAYING) {
              this.pauseVideo(item);
            } else if (item._state === STATE_PREVIEW) {
              this.stopPreview(item);
            }
          });
        });
      }

      syncMutedClass(item) {
        if (!item._video) return;
        // Reflect the user's persistent preference, not the transient
        // video.muted (which is forced true during hover preview).
        const showMuted = !item._userWantsSound;
        item.classList.toggle('is-muted', showMuted);
        if (item._soundButton) {
          item._soundButton.setAttribute('aria-label', showMuted ? SOUND_LABEL_OFF : SOUND_LABEL_ON);
        }
      }
    }
  );
})();
